<template>
  <ContentWrap title="平台概况">
    <div v-loading="loading" class="platform-dashboard">
      <div class="overview-toolbar">
        <div>
          <div class="toolbar-title">全平台酒店运营概览</div>
          <div class="toolbar-subtitle">跨全部酒店的订单、营收、评价与房态聚合指标</div>
        </div>
        <el-button type="primary" :loading="loading" @click="loadAll">
          <Icon class="mr-5px" icon="ep:refresh" />
          刷新
        </el-button>
      </div>

      <el-row :gutter="16">
        <el-col v-for="card in metricCards" :key="card.key" :xs="24" :sm="12" :lg="8" :xl="4">
          <div class="metric-card">
            <div class="metric-icon" :style="{ color: card.color, background: card.bg }">
              <el-icon :size="22"><component :is="card.icon" /></el-icon>
            </div>
            <div class="metric-content">
              <div class="metric-label">{{ card.label }}</div>
              <div class="metric-value">{{ card.value }}</div>
              <div class="metric-note">{{ card.note }}</div>
            </div>
          </div>
        </el-col>
      </el-row>

      <el-row :gutter="16">
        <el-col :xs="24" :lg="8">
          <div class="panel">
            <div class="panel-title">房态总览</div>
            <div class="room-total">
              <span class="room-number">{{ overview.totalRooms }}</span>
              <span class="room-unit">间客房</span>
              <el-tag type="success" effect="plain">入住率 {{ formatPercent(overview.occupancyRate) }}</el-tag>
            </div>
            <el-progress
              :percentage="Number(overview.occupancyRate || 0)"
              :stroke-width="10"
              color="#2f80ed"
              :show-text="false"
            />
            <div class="status-line">
              <span>在住房间</span>
              <strong>{{ overview.occupiedRooms }}</strong>
            </div>
            <div class="status-line">
              <span>空闲房间</span>
              <strong>{{ Math.max(overview.totalRooms - overview.occupiedRooms, 0) }}</strong>
            </div>
          </div>
        </el-col>
        <el-col :xs="24" :lg="8">
          <div class="panel">
            <div class="panel-title">订单与营收</div>
            <div class="status-line">
              <span>累计订单</span>
              <strong>{{ overview.totalOrderCount }}</strong>
            </div>
            <div class="status-line">
              <span>今日新增订单</span>
              <strong>{{ overview.todayOrderCount }}</strong>
            </div>
            <div class="status-line">
              <span>今日营收</span>
              <strong class="money">¥{{ formatMoney(overview.todayRevenue) }}</strong>
            </div>
          </div>
        </el-col>
        <el-col :xs="24" :lg="8">
          <div class="panel">
            <div class="panel-title">评价与告警</div>
            <div class="status-line">
              <span>评价总数</span>
              <strong>{{ overview.totalReviewCount }}</strong>
            </div>
            <div class="status-line">
              <span>待审核评价</span>
              <strong class="warning">{{ overview.pendingReviewCount }}</strong>
            </div>
            <div class="status-line">
              <span>未处理房间告警</span>
              <strong class="danger">{{ overview.unhandledAlertCount }}</strong>
            </div>
          </div>
        </el-col>
      </el-row>

      <el-row :gutter="16">
        <el-col :xs="24" :lg="12">
          <div class="panel">
            <div class="panel-title">近 30 天入住率趋势</div>
            <div ref="occupancyChartRef" class="chart"></div>
          </div>
        </el-col>
        <el-col :xs="24" :lg="12">
          <div class="panel">
            <div class="panel-title">近 30 天营收趋势</div>
            <div ref="revenueChartRef" class="chart"></div>
          </div>
        </el-col>
      </el-row>
    </div>
  </ContentWrap>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from 'vue'
import * as echarts from 'echarts'
import { Bell, Money, Odometer, OfficeBuilding, Star, Tickets } from '@element-plus/icons-vue'
import * as PlatformDashboardApi from '@/api/hotel/platformDashboard'
import type { PlatformOverviewVO } from '@/api/hotel/platformDashboard'
import * as DashboardApi from '@/api/hotel/dashboard'
import type { TrendItemVO } from '@/api/hotel/dashboard'

defineOptions({ name: 'HotelPlatformDashboard' })

const loading = ref(false)
const overview = reactive<PlatformOverviewVO>({
  hotelCount: 0,
  activeHotelCount: 0,
  configuredHotelCount: 0,
  totalOrderCount: 0,
  todayOrderCount: 0,
  todayRevenue: 0,
  pendingReviewCount: 0,
  hiddenReviewCount: 0,
  totalReviewCount: 0,
  unhandledAlertCount: 0,
  totalRooms: 0,
  occupiedRooms: 0,
  occupancyRate: 0
})

const formatMoney = (value?: number) => Number(value || 0).toFixed(2)
const formatPercent = (value?: number) => `${Number(value || 0).toFixed(2)}%`

const metricCards = computed(() => [
  {
    key: 'hotel',
    label: '酒店总数',
    value: String(overview.hotelCount),
    note: `营业中 ${overview.activeHotelCount} 家`,
    icon: OfficeBuilding,
    color: '#2f80ed',
    bg: '#eaf2ff'
  },
  {
    key: 'order',
    label: '今日新增订单',
    value: String(overview.todayOrderCount),
    note: `累计订单 ${overview.totalOrderCount}`,
    icon: Tickets,
    color: '#7c5cff',
    bg: '#f0ecff'
  },
  {
    key: 'revenue',
    label: '今日营收',
    value: `¥${formatMoney(overview.todayRevenue)}`,
    note: '按已支付及之后状态统计',
    icon: Money,
    color: '#00a67d',
    bg: '#e8f8f2'
  },
  {
    key: 'review',
    label: '待审核评价',
    value: String(overview.pendingReviewCount),
    note: `评价总数 ${overview.totalReviewCount}`,
    icon: Star,
    color: '#f59f00',
    bg: '#fdf5e6'
  },
  {
    key: 'alert',
    label: '未处理告警',
    value: String(overview.unhandledAlertCount),
    note: '房间告警待处理',
    icon: Bell,
    color: '#e5484d',
    bg: '#fdeeee'
  },
  {
    key: 'room',
    label: '入住率',
    value: formatPercent(overview.occupancyRate),
    note: `在住 ${overview.occupiedRooms} / ${overview.totalRooms} 间`,
    icon: Odometer,
    color: '#0ea5e9',
    bg: '#e8f6fd'
  }
])

// ==================== 趋势图 ====================
const occupancyChartRef = ref<HTMLDivElement>()
const revenueChartRef = ref<HTMLDivElement>()
let occupancyChart: echarts.ECharts | null = null
let revenueChart: echarts.ECharts | null = null

const handleResize = () => {
  occupancyChart?.resize()
  revenueChart?.resize()
}

const initCharts = () => {
  if (occupancyChartRef.value) {
    occupancyChart = echarts.init(occupancyChartRef.value)
  }
  if (revenueChartRef.value) {
    revenueChart = echarts.init(revenueChartRef.value)
  }
  window.addEventListener('resize', handleResize)
}

const setOccupancyChart = (data: TrendItemVO[]) => {
  occupancyChart?.setOption({
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => `${params[0].axisValue}<br/>入住率: ${params[0].value}%`
    },
    grid: { top: 20, right: 20, bottom: 30, left: 50 },
    xAxis: {
      type: 'category',
      data: data.map((d) => d.date),
      axisLabel: { rotate: 45, fontSize: 11 }
    },
    yAxis: { type: 'value', name: '入住率(%)', min: 0, max: 100 },
    series: [
      {
        name: '入住率',
        type: 'line',
        data: data.map((d) => d.value),
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { color: '#409eff', width: 2 },
        itemStyle: { color: '#409eff' },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
            { offset: 1, color: 'rgba(64, 158, 255, 0.05)' }
          ])
        }
      }
    ]
  })
}

const setRevenueChart = (data: TrendItemVO[]) => {
  revenueChart?.setOption({
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => `${params[0].axisValue}<br/>营收: ¥${Number(params[0].value).toFixed(2)}`
    },
    grid: { top: 20, right: 20, bottom: 30, left: 60 },
    xAxis: {
      type: 'category',
      data: data.map((d) => d.date),
      axisLabel: { rotate: 45, fontSize: 11 }
    },
    yAxis: { type: 'value', name: '营收(元)' },
    series: [
      {
        name: '营收',
        type: 'bar',
        data: data.map((d) => d.value),
        barWidth: '50%',
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#67c23a' },
            { offset: 1, color: '#95d475' }
          ]),
          borderRadius: [4, 4, 0, 0]
        }
      }
    ]
  })
}

// ==================== 数据加载 ====================
const loadAll = async () => {
  loading.value = true
  try {
    const [overviewData, occupancy, revenue] = await Promise.all([
      PlatformDashboardApi.getPlatformOverview(),
      DashboardApi.getOccupancyTrend({}),
      DashboardApi.getRevenueTrend({})
    ])
    if (overviewData) {
      Object.assign(overview, overviewData)
    }
    setOccupancyChart(occupancy || [])
    setRevenueChart(revenue || [])
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  nextTick(() => initCharts())
  loadAll()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  occupancyChart?.dispose()
  revenueChart?.dispose()
})
</script>

<style scoped lang="scss">
.platform-dashboard {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.overview-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border: 1px solid #e8ecf2;
  border-radius: 8px;
}

.toolbar-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2d3d;
}

.toolbar-subtitle {
  margin-top: 4px;
  font-size: 13px;
  color: #8a94a6;
}

.metric-card,
.panel {
  border: 1px solid #e8ecf2;
  border-radius: 8px;
  background: #fff;
}

.metric-card {
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 18px 20px;
  margin-bottom: 16px;
}

.metric-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 10px;
  flex-shrink: 0;
}

.metric-label {
  font-size: 13px;
  color: #8a94a6;
}

.metric-value {
  margin-top: 4px;
  font-size: 22px;
  font-weight: 600;
  color: #1f2d3d;
}

.metric-note {
  margin-top: 4px;
  font-size: 12px;
  color: #a6adbb;
}

.panel {
  padding: 18px 20px;
  margin-bottom: 16px;
}

.panel-title {
  margin-bottom: 14px;
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
}

.room-total {
  display: flex;
  gap: 10px;
  align-items: baseline;
  margin-bottom: 12px;
}

.room-number {
  font-size: 30px;
  font-weight: 600;
  color: #1f2d3d;
}

.room-unit {
  font-size: 13px;
  color: #8a94a6;
}

.status-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 13px;
  color: #606a7b;
  border-bottom: 1px dashed #eef1f5;
}

.status-line:last-child {
  border-bottom: none;
}

.status-line strong {
  font-size: 15px;
  color: #1f2d3d;
}

.status-line .money {
  color: #00a67d;
}

.status-line .warning {
  color: #f59f00;
}

.status-line .danger {
  color: #e5484d;
}

.chart {
  height: 280px;
}
</style>
